/**
 * SearchResultsDropdown — live-search overlay under the Header.
 *
 * Consumes `@ksyk/shared`'s `buildRoomSearchIndex` so it inherits the
 * fuzzy/prefix/typo-tolerant ranking the whole app has agreed on.
 * Purely presentational — the actual search index is built here from
 * the `/api/rooms` + `/api/buildings` snapshots via React Query.
 *
 * Renders under the header's Search input; matches the input's
 * `aria-controls="search-results-listbox"` so screen readers announce
 * results as the user types.
 *
 * Clicking a result flies the map to the room and (optionally) calls
 * `onSelect(room)` — the parent can update its selection state, open
 * a room info sheet, etc.
 */
import { useEffect, useMemo, useState } from "react";
import { buildRoomSearchIndex } from "@ksyk/shared";
import type { Room, Building, SearchHit } from "@ksyk/shared";
import { useDarkMode } from "@/contexts/DarkModeContext";
import { cn } from "@/lib/utils";
import { useCampusData } from "@/hooks/useCampusData";
import { loadRecentPicks, recordPick, clearRecents, type RecentPickStub } from "@/lib/recentSearches";
import { Clock, Search } from "lucide-react";

/** What was clicked in the dropdown — either a room or a building. */
export type SearchPick =
  | { kind: "room"; room: Room; building: Building | null }
  | { kind: "building"; building: Building };

export interface SearchResultsDropdownProps {
  query: string;
  /** Called when the user clicks a result. Receives either a room hit
   *  or a building hit — the map focuses the polygon centroid for both. */
  onSelect: (pick: SearchPick) => void;
  /** Max visible results. Default 8. */
  limit?: number;
  /** Optional custom Y offset (px) from the top of the viewport. Falls
   *  back to the same offset the header search row sits at. */
  offsetTop?: number;
  /** When true AND query is empty, show the "Recent" panel of last
   *  N picks. Parent tracks input focus and passes this through. */
  showRecents?: boolean;
}

export default function SearchResultsDropdown({
  query,
  onSelect,
  limit = 8,
  offsetTop,
  showRecents = false,
}: SearchResultsDropdownProps) {
  const { darkMode } = useDarkMode();

  // Measure the header dynamically instead of assuming 128px — on
  // mobile the announcement banner + safe-area inset + stacked search
  // row push the search input down further, and a hardcoded offset
  // meant the dropdown overlapped the search input on tall layouts.
  const [autoOffset, setAutoOffset] = useState<number>(() => offsetTop ?? 128);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const measure = () => {
      // Header is a sticky div that wraps the whole announcement + top
      // bar + search row. Grab the LAST search input on the page since
      // there might be nested search inputs in nav panel etc.
      const header = document.querySelector<HTMLElement>('header');
      if (!header) return;
      const rect = header.getBoundingClientRect();
      // Breathing gap between the input and the dropdown. Larger on
      // desktop where the header renders as a floating chip (2px
      // outer padding + 1px border), so a 6px gap looks cramped and
      // the dropdown appears to touch the input.
      const gap = window.innerWidth >= 640 ? 14 : 8;
      setAutoOffset(Math.max(0, rect.bottom + gap));
    };
    measure();
    // Recompute on every resize / orientation change so mobile browsers
    // that grow the viewport when the keyboard closes stay in sync.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    // Also observe header size — some CSS transitions animate the
    // announcement banner in/out.
    const header = document.querySelector<HTMLElement>('header');
    let ro: ResizeObserver | null = null;
    if (header && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(header);
    }
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
      ro?.disconnect();
    };
  }, []);
  const effectiveOffset = offsetTop ?? autoOffset;

  const { rooms, buildings } = useCampusData();
  const index = useMemo(() => buildRoomSearchIndex(rooms, buildings), [rooms, buildings]);
  const trimmed = query.trim();

  // Filter chips let users narrow to just rooms / buildings / specific
  // room types. "All" keeps the raw ranked list.
  const [activeFilter, setActiveFilter] = useState<string>("all");
  useEffect(() => {
    // Reset filter when the query changes so the user isn't confused
    // by an old filter narrowing a new query.
    setActiveFilter("all");
  }, [trimmed]);

  // Precompute a raw hit set (up to a higher limit) so filters can
  // narrow client-side without a re-search per chip.
  const rawHits: Array<SearchHit<{ room: Room | null; building: Building | null }>> = useMemo(() => {
    if (!trimmed) return [];
    return index.search(trimmed, { limit: Math.max(limit * 4, 40) });
  }, [index, trimmed, limit]);

  const hits = useMemo(() => {
    const filtered = rawHits.filter((h) => {
      if (activeFilter === "all") return true;
      if (activeFilter === "buildings") return !h.doc.data?.room;
      if (activeFilter === "rooms") return !!h.doc.data?.room;
      // Room-type filter — string equals on Room.type.
      const type = h.doc.data?.room?.type;
      return type === activeFilter;
    });
    return filtered.slice(0, limit);
  }, [rawHits, activeFilter, limit]);

  // Available filter chips depend on what actually appears in the
  // raw results — no point offering "Labs" when the query has no lab
  // hits. Always include All / Rooms / Buildings when both kinds are
  // represented.
  const filterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; count: number }> = [
      { id: "all", label: "All", count: rawHits.length },
    ];
    const roomsCount = rawHits.filter((h) => h.doc.data?.room).length;
    const buildingsCount = rawHits.filter((h) => !h.doc.data?.room).length;
    if (roomsCount > 0 && buildingsCount > 0) {
      chips.push({ id: "rooms", label: "Rooms", count: roomsCount });
      chips.push({ id: "buildings", label: "Buildings", count: buildingsCount });
    }
    // Common room-type chips — only surface when there's at least one hit.
    const typesToOffer: Array<[string, string]> = [
      ["classroom", "Classrooms"],
      ["lab", "Labs"],
      ["bathroom", "Bathrooms"],
      ["cafeteria", "Cafés"],
      ["gym", "Gyms"],
      ["office", "Offices"],
      ["elevator", "Elevators"],
      ["stairs", "Stairs"],
      ["entrance", "Entrances"],
    ];
    for (const [id, label] of typesToOffer) {
      const c = rawHits.filter((h) => h.doc.data?.room?.type === id).length;
      if (c > 0) chips.push({ id, label, count: c });
    }
    return chips;
  }, [rawHits]);

  // Wrap the caller's onSelect so every pick lands in the recents
  // store — enables the empty-state list below without leaking the
  // dep into every consumer.
  const handleSelect = (pick: SearchPick) => {
    recordPick(pick);
    onSelect(pick);
  };

  // Track focus on the combobox input so recents only appear when
  // the user is actually engaging with search. The header owns the
  // input but exposes `role="combobox" aria-controls="search-results-listbox"` —
  // we listen for focus/blur on that element directly.
  const [inputFocused, setInputFocused] = useState(false);
  useEffect(() => {
    const input = document.querySelector<HTMLInputElement>('[aria-controls="search-results-listbox"]');
    if (!input) return;
    const onFocus = () => setInputFocused(true);
    const onBlur = () => {
      // Delay blur so a click on a result row (which blurs the input)
      // doesn't dismiss the recents list before the click registers.
      setTimeout(() => setInputFocused(false), 120);
    };
    input.addEventListener("focus", onFocus);
    input.addEventListener("blur", onBlur);
    // If already focused when we mount.
    if (document.activeElement === input) setInputFocused(true);
    return () => {
      input.removeEventListener("focus", onFocus);
      input.removeEventListener("blur", onBlur);
    };
  }, []);

  // Recents to render on empty query when the input is focused.
  const [recents, setRecents] = useState<RecentPickStub[]>(() => loadRecentPicks());
  useEffect(() => {
    // Refresh when opening the empty-state panel so a pick from
    // another tab or the info-sheet shows up.
    if ((showRecents || inputFocused) && !trimmed) setRecents(loadRecentPicks());
  }, [showRecents, inputFocused, trimmed]);
  const shouldShowRecents = (showRecents || inputFocused) && recents.length > 0;

  // Nothing to render if no query AND no recents-panel requested.
  if (!trimmed && !shouldShowRecents) return null;

  return (
    <div
      id="search-results-listbox"
      role="listbox"
      aria-label={`${hits.length} search results`}
      className={cn(
        // Full-width on mobile with side gutters, capped at 42 rem on
        // larger viewports. Wilma-style: hairline border, 8 px radius,
        // no backdrop blur, tighter shadow.
        "fixed left-2 right-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[55] sm:w-[min(92vw,42rem)] rounded-[8px] border overflow-hidden",
        darkMode
          ? "bg-gray-950 border-[#2a3040] text-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
          : "bg-white border-[#d5dae0] text-gray-900 shadow-[0_10px_30px_rgba(15,23,42,0.18)]",
      )}
      style={{
        top: effectiveOffset,
        maxHeight: `calc(100dvh - ${effectiveOffset}px - env(safe-area-inset-bottom, 0px) - 12px)`,
      }}
    >
      {/* Filter chips — hidden when only "All" would show, since a
       *  single chip is just noise. */}
      {filterChips.length > 1 && (
        <div className={cn(
          "flex gap-1 overflow-x-auto px-3 py-2 border-b",
          darkMode ? "border-[#2a3040] bg-[#12161f]" : "border-[#d5dae0] bg-[#f5f6f8]",
        )}>
          {filterChips.map((chip) => {
            const active = activeFilter === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setActiveFilter(chip.id)}
                className={cn(
                  "shrink-0 text-[11px] font-bold uppercase tracking-[0.04em] px-2 h-7 rounded-[4px] transition-colors inline-flex items-center gap-1.5",
                  active
                    ? "bg-[#003d82] text-white"
                    : darkMode
                      ? "bg-gray-950 text-gray-300 hover:bg-gray-900 border border-[#2a3040]"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-[#d5dae0]",
                )}
              >
                {chip.label}
                <span className={cn(
                  "text-[10px] tabular-nums font-bold",
                  active ? "text-white/80" : darkMode ? "text-gray-500" : "text-gray-500",
                )}>{chip.count}</span>
              </button>
            );
          })}
        </div>
      )}
      {!trimmed && shouldShowRecents ? (
        <div>
          {/* v4.7.49 — recent-searches header uses the same styling as
           *  the filter-chips band above so the dropdown feels like one
           *  coherent surface regardless of state. */}
          <div className={cn(
            "flex items-center gap-2 px-3 py-2 border-b",
            darkMode ? "border-[#2a3040] bg-[#12161f]" : "border-[#d5dae0] bg-[#f5f6f8]",
          )}>
            <Clock className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" strokeWidth={2.25} />
            <p className="text-[10px] font-bold tracking-[0.08em] uppercase text-gray-500 dark:text-gray-400 flex-1">
              Recent
            </p>
            <button
              type="button"
              onClick={() => { clearRecents(); setRecents([]); }}
              className="text-[10px] font-bold uppercase tracking-[0.04em] text-[#003d82] dark:text-[#4a90d9] hover:underline"
            >
              Clear
            </button>
          </div>
          <ul className={cn(
            "divide-y",
            darkMode ? "divide-[#2a3040]" : "divide-[#d5dae0]",
          )}>
            {recents.map((r) => {
              // Resolve back to a live entity so the pick fly-to has
              // fresh coordinates. If the entity was deleted since
              // recording, we skip the row.
              const building = r.kind === "building"
                ? buildings.find((b) => b.id === r.id) ?? null
                : (r.buildingId ? buildings.find((b) => b.id === r.buildingId) : null) ?? null;
              const room = r.kind === "room"
                ? rooms.find((x) => x.id === r.id) ?? null
                : null;
              const canPick = (r.kind === "building" && building) || (r.kind === "room" && room);
              if (!canPick) return null;
              const pick: SearchPick = r.kind === "building"
                ? { kind: "building", building: building! }
                : { kind: "room", room: room!, building };
              return (
                <li
                  key={`${r.kind}:${r.id}`}
                  role="option"
                  aria-selected="false"
                  onClick={() => handleSelect(pick)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors",
                    darkMode ? "hover:bg-[#4a90d9]/10" : "hover:bg-[#e6ecf3]",
                  )}
                >
                  <TypeChip pick={pick} darkMode={darkMode} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{r.title}</div>
                    {r.subtitle && (
                      <div className={cn("text-xs truncate", darkMode ? "text-gray-400" : "text-gray-500")}>
                        {r.subtitle}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : hits.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <div className={cn(
            "mx-auto h-10 w-10 rounded-[6px] flex items-center justify-center mb-3 border",
            darkMode ? "bg-gray-900 border-[#2a3040] text-gray-500" : "bg-[#f5f6f8] border-[#d5dae0] text-gray-500",
          )}>
            <Search className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <p className={cn("text-[13px] font-bold", darkMode ? "text-gray-200" : "text-gray-900")}>
            No matches for &quot;{trimmed}&quot;
          </p>
          <p className={cn("text-[11px] mt-1", darkMode ? "text-gray-400" : "text-gray-500")}>
            Try a room number, building name, or a shorter query.
          </p>
        </div>
      ) : (
        <ul className="overflow-y-auto divide-y divide-inherit" style={{ maxHeight: `calc(100dvh - ${effectiveOffset}px - env(safe-area-inset-bottom, 0px) - 12px)` }}>
          {hits.map((hit) => {
            const room = hit.doc.data!.room;
            const building = hit.doc.data!.building;
            const pick: SearchPick = room
              ? { kind: "room", room, building }
              : { kind: "building", building: building! };
            return (
              <li
                key={hit.id}
                role="option"
                aria-selected="false"
                onClick={() => handleSelect(pick)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors",
                  darkMode
                    ? "hover:bg-blue-500/10 hover:text-blue-200"
                    : "hover:bg-blue-50 hover:text-blue-800",
                )}
              >
                <TypeChip pick={pick} darkMode={darkMode} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">
                    <HighlightedText text={hit.doc.title} highlights={hit.highlights.filter((h) => h.field === "title")} />
                  </div>
                  {hit.doc.subtitle && (
                    <div className={cn("text-xs truncate", darkMode ? "text-gray-400" : "text-gray-500")}>
                      <HighlightedText text={hit.doc.subtitle} highlights={hit.highlights.filter((h) => h.field === "subtitle")} />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Render `text` with highlight ranges wrapped in <mark> for the
 *  user's query hits. Non-highlighted spans render as plain text. */
function HighlightedText({
  text,
  highlights,
}: {
  text: string;
  highlights: Array<{ start: number; end: number }>;
}) {
  if (!highlights.length) return <>{text}</>;
  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const parts: Array<{ text: string; hit: boolean }> = [];
  let cursor = 0;
  for (const h of sorted) {
    if (h.start > cursor) parts.push({ text: text.slice(cursor, h.start), hit: false });
    parts.push({ text: text.slice(h.start, h.end), hit: true });
    cursor = h.end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), hit: false });
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <mark
            key={i}
            className="bg-yellow-200/70 dark:bg-yellow-500/25 text-inherit rounded-sm px-0.5"
          >
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

/** Tiny circular icon chip identifying the pick's kind. Uses the same
 *  semantic colours as the POI icons on the map so users get a
 *  consistent visual language across search + map. */
function TypeChip({ pick, darkMode }: { pick: SearchPick; darkMode: boolean }) {
  const cfg = pickChip(pick);
  return (
    <span
      className="h-8 min-w-[36px] px-1.5 rounded-[4px] flex items-center justify-center text-[11px] font-bold shrink-0 tracking-tight"
      style={{
        background: darkMode ? `${cfg.bg}33` : cfg.bg,
        color: cfg.fg,
      }}
      aria-hidden="true"
    >
      {cfg.glyph}
    </span>
  );
}

function pickChip(pick: SearchPick): { bg: string; fg: string; glyph: string } {
  if (pick.kind === "building") return { bg: "#dbeafe", fg: "#1e40af", glyph: "B" };
  const type = pick.room.type;
  switch (type) {
    case "classroom":    return { bg: "#e0f2fe", fg: "#0369a1", glyph: "K" };
    case "lab":          return { bg: "#ede9fe", fg: "#6d28d9", glyph: "Lab" };
    case "bathroom":     return { bg: "#e0f2fe", fg: "#0f766e", glyph: "WC" };
    case "locker_room":  return { bg: "#f3f4f6", fg: "#374151", glyph: "LK" };
    case "cafeteria":    return { bg: "#fef3c7", fg: "#b45309", glyph: "Cafe" };
    case "gym":          return { bg: "#dcfce7", fg: "#15803d", glyph: "Gym" };
    case "office":       return { bg: "#f3f4f6", fg: "#4b5563", glyph: "Off" };
    case "elevator":     return { bg: "#dbeafe", fg: "#1d4ed8", glyph: "⇕" };
    case "stairs":       return { bg: "#fef3c7", fg: "#b45309", glyph: "⇈" };
    case "entrance":     return { bg: "#dcfce7", fg: "#15803d", glyph: "In" };
    case "exit":         return { bg: "#fee2e2", fg: "#b91c1c", glyph: "Out" };
    case "library":      return { bg: "#fef3c7", fg: "#b45309", glyph: "Lib" };
    case "auditorium":   return { bg: "#f5f3ff", fg: "#6d28d9", glyph: "Aud" };
    case "storage":
    case "mechanical":   return { bg: "#f3f4f6", fg: "#6b7280", glyph: "Str" };
    case "lobby":        return { bg: "#e0f2fe", fg: "#0369a1", glyph: "Lob" };
    default:             return { bg: "#e0f2fe", fg: "#0f766e", glyph: "R" };
  }
}
